import Sentry from "@similarweb/sentry-logger";

import { entitiesArray, entitiesObject } from "./constants";
import type { IWebEntity, WebEntityId, WebEntityStorageLabel } from "./types";

interface IEntitiesObjectValue {
    [key: string]: string[];
}

const ENTITY_STORAGE_SEPARATOR = ": ";

const isWebEntityStorageLabel = (platform: string): platform is WebEntityStorageLabel =>
    Object.prototype.hasOwnProperty.call(entitiesArray, platform);

export const buildEntityStorageString = (entityId: WebEntityId, value: string): string =>
    `${entitiesObject[entityId]}${ENTITY_STORAGE_SEPARATOR}${value}`;

const parseEntityStorageString = (item: string): { platform: string; value: string } | null => {
    const separatorIndex = item.indexOf(ENTITY_STORAGE_SEPARATOR);
    if (separatorIndex === -1) {
        return null;
    }
    return {
        platform: item.slice(0, separatorIndex),
        value: item.slice(separatorIndex + ENTITY_STORAGE_SEPARATOR.length),
    };
};

export const formatEntityForDisplay = (item: string, entities: IWebEntity[]): string => {
    const parsed = parseEntityStorageString(item);
    if (!parsed || !isWebEntityStorageLabel(parsed.platform)) {
        return item;
    }

    const entityId = entitiesArray[parsed.platform];
    const entityConfig = entities.find((entity) => entity.id === entityId);
    if (!entityConfig) {
        return item;
    }

    return `${entityConfig.chipText}${ENTITY_STORAGE_SEPARATOR}${parsed.value}`;
};

export const entitiesObjectToArray = (value: IEntitiesObjectValue) => {
    const array: string[] = [];

    for (const key in value) {
        if (value[key].length > 0 && entitiesObject[key]) {
            value[key].map((item) => {
                array.push(`${entitiesObject[key]}: ${item}`);
            });
        } else if (value[key].length > 0) {
            array.push(`${key}: ${value[key].join(", ")}`);
        }
    }
    return array;
};

export const entitiesArrayToObject = (value: string[]) => {
    const entities = {
        googleBusinessProfiles: [] as string[],
        youTubeChannels: [] as string[],
        tikTokChannels: [] as string[],
        twitterAccounts: [] as string[],
    };

    value?.forEach((item) => {
        const parsed = parseEntityStorageString(item);
        if (!parsed) {
            return;
        }
        if (!isWebEntityStorageLabel(parsed.platform)) {
            Sentry.warning("Rank Tracker web entity: unknown platform prefix", {
                platform: parsed.platform,
            });
            return;
        }
        const key = entitiesArray[parsed.platform];
        entities[key].push(parsed.value);
    });

    return entities;
};
